import { CommonModule } from '@angular/common';
import { Component, Inject, Input, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { switchMap, take } from 'rxjs/operators';

import { ApiComingSoonBackSoonService } from '@api/_api/api-coming-soon-back-soon/api-coming-soon-back-soon.service';
import { Product } from '@api/catalog/products/products.models';
import { ComingSoonBackSoonRequestBody } from '@api/coming-soon-back-soon/coming-soon-back-soon.models';
import { ComingSoonBackSoonService } from '@api/coming-soon-back-soon/coming-soon-back-soon.service';
import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { MarketConfigurable } from '@core/domain/impl/market-configurable.impl';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { StoreAndLanguage } from '@core/domain/state/core.state';
import { getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { NotificationPublicPort } from '@features/notification';
import { SidebarPublicPort } from '@features/sidebar';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { AnalyticsEvent, SendBasicAnalytics } from '@shared/analytics/store/analytics.actions';
import { EventFormErrorAnalytics, OpenPopUpCsbsAnalytics, SubscribeComingSoonBackSoonAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import * as relatedProductsLoader from '@shared/product/related-products/application/related-products-loader.service';
import { LazyLoadService } from '@shared/services/lazy-load.service';
import { InputTermsSections } from '@shared/terms-conditions/components/input-terms-conditions/input-terms-conditions.component';
import { AcceptanceService } from '@shared/terms-conditions/services/acceptance.service';
import { TermsConditionsModule } from '@shared/terms-conditions/terms-conditions.module';
import { UiSvgIconModule } from '@shared/ui/svg-icon/svg-icon.module';

@Component({
    selector: 'product-coming-soon',
    templateUrl: './product-coming-soon.component.html',
    styleUrls: ['./product-coming-soon.component.scss'],
    providers: [
        ComingSoonBackSoonService,
        ApiComingSoonBackSoonService,
    ],
    imports: [
        TermsConditionsModule,
        CommonModule,
        TranslateModule,
        FormsModule,
        ReactiveFormsModule,
        UiSvgIconModule,
    ],
    standalone: true,
})
export class ProductComingSoonComponent extends MarketConfigurable<Record<string, boolean>> implements OnInit {
    @Input() public product: Product;

    @Input() public countryCode: string;

    public form: UntypedFormGroup;

    public stockAvailabilitySuccess = false;

    public isActiveTerms: boolean = false;

    public displayRelatedProducts: boolean = false;

    public termsConditionsSections: InputTermsSections[];

    private kind: string;

    private relatedProductsLoaded: boolean = false;

    protected readonly config: Record<string, Record<string, boolean>> = {
        CN: {
            showSensitiveDataConditions: true,
        },
        default: {
            showSensitiveDataConditions: false,
        },
    };

    private readonly hidePriceText: boolean = true;

    @ViewChild('relatedProducts', { read: ViewContainerRef })
    private set watch(relatedProductsContainer: ViewContainerRef) {
        if (!this.relatedProductsLoaded) {
            if (relatedProductsContainer) {
                this.lazyLoadRelatedProducts(relatedProductsContainer);
            }
        }
        this.relatedProductsLoaded = true;
    }

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly comingSoonBackSoonService: ComingSoonBackSoonService,
        private readonly acceptanceService: AcceptanceService,
        private readonly store: Store,
        private readonly translateService: TranslateService,
        private readonly lazyLoadService: LazyLoadService,
        @Inject(WINDOW) private readonly window: CommonWindow,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.store.dispatch(OpenPopUpCsbsAnalytics({
            product: this.product,
        }));
        this.kind = this.product.status.selectedSize.visibilityValue.toLowerCase();
        this.initForm();

        this.termsConditionsSections = this.generateTermsConditionsSections();
    }

    private initForm(): void {
        const emailRegex = RegExp(/^(([^\s"(),.:;<>@[\]]+(\.[^\s"(),.:;<>@[\]]+)*)|(".+"))@(([^\s"(),.:;<>@[\]]+\.)+[^\s"(),.:;<>@[\]]{2,})$/i);

        this.form = this.formBuilder.group({
            email: new UntypedFormControl('', [Validators.required, Validators.pattern(emailRegex)]),
        });

        this.displayRelatedProducts = true;
    }

    private generateTermsConditionsSections(): InputTermsSections[] {
        const sections = [];

        const configTerms = this.getConfigByMarketCode(this.countryCode);

        sections.push(
            { type: 'productStatusConditions', required: true }
        );

        if (configTerms.showSensitiveDataConditions) {
            sections.push(
                { type: 'sensitiveDataConditions', required: true }
            );
        }

        return sections;
    }

    public addFormControl(name: string, formGroup: UntypedFormGroup): void {
        if (this.form.controls[name]) {
            this.form.removeControl(name, { emitEvent: false });
        }
        this.form.addControl(name, formGroup, { emitEvent: false });
    }

    public close(): void {
        SidebarPublicPort(this.store).closeSidebar();
        this.displayRelatedProducts = false;
    }

    public clickOnPrivacyPolicy(): void {
        this.store.dispatch(SendBasicAnalytics({
            cf: 'legal',
            category: this.kind,
            action: 'ver_politica_privacidad',
            eventFrom: EventFrom.comingSoon,
        }));
    }

    public changeEmail(): void {
        if (this.form.controls.email.invalid) {
            this.store.dispatch(EventFormErrorAnalytics({
                cf: 'checkout',
                category: this.kind,
                formName: `${this.kind}_form`,
                form: [{
                    name: 'email',
                    value: this.form.controls.email.value,
                    valid: this.form.controls.email.valid,
                }],
                eventFrom: EventFrom.comingSoon,
            }));
        }
    }

    public submit(): void {
        const email = this.form.value.email;
        const suscriptionData: ComingSoonBackSoonRequestBody = {
            email,
            quantity: 1,
            sku: +this.product.status.selectedSize.sku,
        };

        // TODO ECOMDUTI-21031 [TechDebt]: Refactorizar
        if (this.isActiveTerms) {
            this.store.select(getStoreAndLanguage).pipe(
                switchMap((storeAndLanguage: StoreAndLanguage) =>
                    this.acceptanceService.consent('COMING_SOON', ['transferenciaforzosa'], email, storeAndLanguage.storeId))
            ).subscribe(() => {
                // WTF??
            });
        }

        this.comingSoonBackSoonService.subscribeTocomingSoonBackSoon(this.product.id, suscriptionData).pipe(
            take(1)
        ).subscribe(() => {
            const message = this.translateService.instant('dev.product.comingSoon.added', { $0: email });
            NotificationPublicPort(this.store).openNotification({ message });

            const color = this.product.status.selectedColor.id;
            const partnumber = `${this.product.status.selectedSize.partnumber}_${color}_${this.product.status.selectedSize.name}`;
            this.store.dispatch(AnalyticsEvent({
                eventData: {
                    cf: 'checkout',
                    category: this.kind,
                    action: 'crear_notificacion',
                    label: partnumber,
                    eventFrom: EventFrom.comingSoon,
                },
            }));

            this.store.dispatch(SubscribeComingSoonBackSoonAnalytics({
                product: this.product,
            }));

            this.close();
        });
    }

    @BrowserOnly<ProductComingSoonComponent, undefined>(undefined)
    private lazyLoadRelatedProducts(relatedProductsContainer: ViewContainerRef) {
        relatedProductsLoader.loadRelatedsModuleOnSectionView(
            this.lazyLoadService,
            this.window,
            relatedProductsContainer,
            {
                place: 'CS_BS',
                baseProducts: [this.product],
                hidePriceText: this.hidePriceText,
                slidesPerView: 2.5,
            }
        );
    }
}
